import { getPartesFilterDay, restoreDailyPartesFilters } from "../../utils/dailyPartesFilters";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Badge,
	Button,
	Checkbox,
	FormControl,
	Grid,
	InputLabel,
	LinearProgress,
	ListItemText,
	MenuItem,
	Pagination,
	Select,
	TextField,
	Typography
} from '@mui/material';
import { saveAs } from 'file-saver';
import { useLocation, useNavigate } from 'react-router-dom';

import CloudOffIcon from '@mui/icons-material/CloudOff';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

import { FiltrosCuadrillas } from './FiltrosCuadrillas';
import { SubHeader } from '../../utils/componentes-utiles/SubHeader';
import {
	DEFAULT_PARTES_GRUPO_TABLE_COLUMNS,
	PARTES_GRUPO_TABLE_COLUMNS,
	TablaCuadrillas,
} from './TablaCuadrillas';
import {
	getCuadrillas,
	getDetalleParteGrupo,
	getUsuarioPreferencias,
	saveUsuarioPreferencias,
} from '../../services/apis';
import { createPartesGrupoExcelWorkbook } from '../../utils/exportPartesGrupoExcel';
import { loadRouteAccessSnapshot } from '../../services/routeAccess';
import { buildCuadrillasCapabilities } from './cuadrillasCapabilities';
import { getAuthStorageUserScope } from '../../services/authStorage';
const CUADRILLAS_SESSION_STATE_KEY = 'planaett:cuadrillas:session-state';
const CUADRILLAS_TABLE_COLUMNS_SESSION_KEY = 'planaett:cuadrillas:table-columns';
const PREF_CUADRILLAS_SESSION_STATE = 'cuadrillas.session-state';
const PREF_CUADRILLAS_ADVANCED_FILTERS = 'cuadrillas.advanced-filters';
const PREF_CUADRILLAS_TABLE_COLUMNS = 'cuadrillas.table-columns';

const buildUserSessionKey = (baseKey) =>
	`${baseKey}:${encodeURIComponent(getAuthStorageUserScope())}`;

const readUserScopedSessionValue = (baseKey) => {
	const scopedValue = window.sessionStorage.getItem(buildUserSessionKey(baseKey));
	return scopedValue !== null ? scopedValue : window.sessionStorage.getItem(baseKey);
};

const writeUserScopedSessionValue = (baseKey, value) => {
	window.sessionStorage.setItem(buildUserSessionKey(baseKey), value);
};

/**
 * Devuelve YYYY-MM-DD usando zona local
 */
const formatLocalDate = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

/**
 * Devuelve DD/MM/YYYY para mostrar bonito
 */
const formatDisplayDate = (dateString) => {
	if (!dateString) return '';
	const [year, month, day] = dateString.split('-');
	return `${day}/${month}/${year}`;
};

const normalizeDateOnly = (value) => {
	if (!value) return '';

	if (typeof value === 'string') {
		const trimmedValue = value.trim();
		const isoMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);

		if (isoMatch) {
			return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
		}

		const esMatch = trimmedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
		if (esMatch) {
			return `${esMatch[3]}-${esMatch[2]}-${esMatch[1]}`;
		}
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';

	return formatLocalDate(date);
};

const filterCuadrillasByDateRange = (items, desde, hasta) => {
	if (!Array.isArray(items)) return [];

	return items.filter((cuadrilla) => {
		const fechaCuadrilla = normalizeDateOnly(cuadrilla?.datos_cuadrilla?.fecha_cuadrilla);
		return fechaCuadrilla && fechaCuadrilla >= desde && fechaCuadrilla <= hasta;
	});
};

const getDefaultFechaDesde = () => {
	const date = new Date();
	date.setFullYear(date.getFullYear());
	return formatLocalDate(date);
};

const getDefaultFechaHasta = () => {
	return formatLocalDate(new Date());
};

const shiftIsoDateByDays = (value, days) => {
	if (!value) return '';

	const [year, month, day] = String(value).split('-').map(Number);
	if (!year || !month || !day) return value;

	const date = new Date(year, month - 1, day);
	date.setDate(date.getDate() + Number(days || 0));

	return formatLocalDate(date);
};

const readCuadrillasSessionState = () => {
	try {
		const rawValue = readUserScopedSessionValue(CUADRILLAS_SESSION_STATE_KEY);
		if (!rawValue) return null;

		const parsedValue = JSON.parse(rawValue);
		return parsedValue && typeof parsedValue === 'object' ? parsedValue : null;
	} catch (error) {
		return null;
	}
};

const persistCuadrillasSessionState = (state) => {
	try {
		writeUserScopedSessionValue(CUADRILLAS_SESSION_STATE_KEY, JSON.stringify(state));
	} catch (error) {
		// Ignore session persistence failures in the UI.
	}
};

const readCuadrillasAdvancedFiltersSessionState = () => {
	try {
		const rawValue = readUserScopedSessionValue('planaett:cuadrillas:advanced-filters');
		if (!rawValue) return [];

		const parsedValue = JSON.parse(rawValue);
		return Array.isArray(parsedValue) ? parsedValue : [];
	} catch (error) {
		return [];
	}
};

const normalizeVisibleTableColumns = (columns) => {
	const allowedColumns = new Set(PARTES_GRUPO_TABLE_COLUMNS.map((column) => column.key));
	const normalizedColumns = (Array.isArray(columns) ? columns : [])
		.map((column) => String(column))
		.filter((column) => allowedColumns.has(column));

	return normalizedColumns.length > 0
		? normalizedColumns
		: DEFAULT_PARTES_GRUPO_TABLE_COLUMNS;
};

const readCuadrillasTableColumnsSessionState = () => {
	try {
		const rawValue = readUserScopedSessionValue(CUADRILLAS_TABLE_COLUMNS_SESSION_KEY);
		if (!rawValue) return DEFAULT_PARTES_GRUPO_TABLE_COLUMNS;

		return normalizeVisibleTableColumns(JSON.parse(rawValue));
	} catch (error) {
		return DEFAULT_PARTES_GRUPO_TABLE_COLUMNS;
	}
};

const persistCuadrillasTableColumnsSessionState = (columns) => {
	try {
		writeUserScopedSessionValue(
			CUADRILLAS_TABLE_COLUMNS_SESSION_KEY,
			JSON.stringify(normalizeVisibleTableColumns(columns))
		);
	} catch (error) {
		// Ignore session persistence failures in the UI.
	}
};

const appendLegacyOperativoFilter = (filters = [], legacyValue = 'todos') => {
	const normalizedFilters = Array.isArray(filters) ? filters : [];

	if (normalizedFilters.some((filterItem) => filterItem?.key === 'operativo')) {
		return normalizedFilters;
	}

	if (legacyValue === 'operativos') {
		return [...normalizedFilters, { key: 'operativo', seleccionado: ['1'] }];
	}

	if (legacyValue === 'no_operativos') {
		return [...normalizedFilters, { key: 'operativo', seleccionado: ['0'] }];
	}

	return normalizedFilters;
};

const buildCuadrillasRestoreState = ({ routeState = null, pageState = null } = {}) => ({
	...(pageState || {}),
	...(routeState || {}),
	advancedFilters: Object.prototype.hasOwnProperty.call(routeState || {}, 'advancedFilters')
		? routeState.advancedFilters
		: pageState?.advancedFilters,
	filtroAplicado: {
		...(pageState?.filtroAplicado || {}),
		...(routeState?.filtroAplicado || {}),
	},
});

export const Cuadrillas = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const routeRestoreState = location.state?.cuadrillasReturnState || null;
	const sessionRestoreState = readCuadrillasSessionState();
	const persistedAdvancedFilters = readCuadrillasAdvancedFiltersSessionState();
	const [filterDay] = useState(getPartesFilterDay);
	const restoreState = restoreDailyPartesFilters(buildCuadrillasRestoreState({
		routeState: routeRestoreState,
		pageState: sessionRestoreState,
	}), filterDay);
	const restoreAdvancedFilters =
		appendLegacyOperativoFilter(
			Array.isArray(restoreState?.advancedFilters)
				? restoreState.advancedFilters
				: persistedAdvancedFilters,
			restoreState?.filtroOperativo || 'todos'
		);
	const [itemsPerPage, setItemsPerPage] = useState(Number(restoreState?.itemsPerPage || 25));
	const [textoBusqueda, setTextoBusqueda] = useState(restoreState?.textoBusqueda || '');

	// Fechas editables en inputs
	const [fechaDesde, setFechaDesde] = useState(
		restoreState?.fechaDesde || getDefaultFechaDesde()
	);
	const [fechaHasta, setFechaHasta] = useState(
		restoreState?.fechaHasta || getDefaultFechaHasta()
	);

	// Fechas aplicadas realmente al filtro / consulta
	const [filtroAplicado, setFiltroAplicado] = useState({
		fechaDesde: restoreState?.filtroAplicado?.fechaDesde || restoreState?.fechaDesde || getDefaultFechaDesde(),
		fechaHasta: restoreState?.filtroAplicado?.fechaHasta || restoreState?.fechaHasta || getDefaultFechaHasta(),
	});

	const [cuadrillas, setCuadrillas] = useState([]);
	const [cuadrillasOG, setCuadrillasOG] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [currentPage, setCurrentPage] = useState(Number(restoreState?.currentPage || 1));
	const [open, setOpen] = useState(null);
	const [openFiltros, setOpenFiltros] = useState(Boolean(restoreState?.openFiltros));
	const [cantidadFiltros, setCantidadFiltros] = useState(0);
	const [filtrosAvanzadosCompartidos, setFiltrosAvanzadosCompartidos] = useState(
		restoreAdvancedFilters
	);
	const [resetFiltrosAvanzadosToken, setResetFiltrosAvanzadosToken] = useState(0);
	const [visibleTableColumns, setVisibleTableColumns] = useState(() =>
		normalizeVisibleTableColumns(
			restoreState?.visibleTableColumns || readCuadrillasTableColumnsSessionState()
		)
	);
	const [remotePreferencesReady, setRemotePreferencesReady] = useState(false);
	const [errorFechas, setErrorFechas] = useState('');
	const [selectedCuadrillas, setSelectedCuadrillas] = useState({});
	const [exporting, setExporting] = useState(false);
	const [exportFeedback, setExportFeedback] = useState(null);
	const [routeSnapshot, setRouteSnapshot] = useState(null);
	const latestRequestRef = useRef(0);

	const capabilities = useMemo(
		() => buildCuadrillasCapabilities(routeSnapshot),
		[routeSnapshot]
	);
	const filtroOperativo = useMemo(() => {
		const filtro = (Array.isArray(filtrosAvanzadosCompartidos)
			? filtrosAvanzadosCompartidos
			: []
		).find((filterItem) => filterItem?.key === 'operativo');
		const selectedValues = (Array.isArray(filtro?.seleccionado) ? filtro.seleccionado : []).filter(
			(value) => String(value) !== '__all__'
		);

		if (selectedValues.includes('1')) {
			return 'operativos';
		}

		if (selectedValues.includes('0')) {
			return 'no_operativos';
		}

		return 'todos';
	}, [filtrosAvanzadosCompartidos]);

	const fechasValidas = useMemo(() => {
		if (!fechaDesde || !fechaHasta) return false;
		return fechaDesde <= fechaHasta;
	}, [fechaDesde, fechaHasta]);

	const cargarCuadrillas = async (desde, hasta) => {
		const requestId = latestRequestRef.current + 1;
		latestRequestRef.current = requestId;

		try {
			setLoaded(false);
			setErrorFechas('');

			if (!desde || !hasta) {
				setCuadrillas([]);
				setCuadrillasOG([]);
				setErrorFechas('Debes indicar ambas fechas.');
				setLoaded(true);
				return;
			}

			if (desde > hasta) {
				setCuadrillas([]);
				setCuadrillasOG([]);
				setErrorFechas('La fecha desde no puede ser mayor que la fecha hasta.');
				setLoaded(true);
				return;
			}

			// Si tu API acepta solo YYYY-MM-DD, usa:
			const response = await getCuadrillas(desde, hasta);

			// Si tu API necesita datetime completos, usa esto en su lugar:
			// const rango = buildRangeForApi(desde, hasta);
			// const response = await getCuadrillas(rango.desde, rango.hasta);

			if (response?.success) {
				if (latestRequestRef.current !== requestId) {
					return;
				}

				const data = filterCuadrillasByDateRange(response.data || [], desde, hasta);
				setCuadrillas(data);
				setCuadrillasOG(data);
				setSelectedCuadrillas({});
				setExportFeedback(null);
			} else {
				if (latestRequestRef.current !== requestId) {
					return;
				}

				setCuadrillas([]);
				setCuadrillasOG([]);
				setSelectedCuadrillas({});
			}
		} catch (error) {
			if (latestRequestRef.current !== requestId) {
				return;
			}

			console.error('Error al cargar cuadrillas:', error);
			setCuadrillas([]);
			setCuadrillasOG([]);
			setSelectedCuadrillas({});
		} finally {
			if (latestRequestRef.current === requestId) {
				setLoaded(true);
			}
		}
	};

	useEffect(() => {
		cargarCuadrillas(filtroAplicado.fechaDesde, filtroAplicado.fechaHasta);
	}, [filtroAplicado]);

	useEffect(() => {
		let active = true;

		const cargarAccesos = async () => {
			try {
				const snapshot = await loadRouteAccessSnapshot();

				if (!active) {
					return;
				}

				setRouteSnapshot(snapshot);
			} catch (error) {
				console.error('Error cargando accesos de cuadrillas:', error);

				if (active) {
					setRouteSnapshot(null);
				}
			}
		};

		cargarAccesos();

		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		setCurrentPage(1);
	}, [textoBusqueda]);

	useEffect(() => {
		persistCuadrillasSessionState({
			filterDay,
			textoBusqueda,
			fechaDesde,
			fechaHasta,
			filtroAplicado,
			itemsPerPage,
			currentPage,
			openFiltros,
			advancedFilters: filtrosAvanzadosCompartidos,
			visibleTableColumns,
		});
	}, [currentPage, fechaDesde, fechaHasta, filtroAplicado, filtrosAvanzadosCompartidos, itemsPerPage, openFiltros, textoBusqueda, visibleTableColumns]);

	useEffect(() => {
		persistCuadrillasTableColumnsSessionState(visibleTableColumns);
	}, [visibleTableColumns]);

	useEffect(() => {
		let active = true;

		const loadRemotePreferences = async () => {
			try {
				const response = await getUsuarioPreferencias([
					PREF_CUADRILLAS_SESSION_STATE,
					PREF_CUADRILLAS_ADVANCED_FILTERS,
					PREF_CUADRILLAS_TABLE_COLUMNS,
				]);
				if (!active || response?.success !== true) {
					return;
				}

				const preferences = response?.preferences || {};
				const remoteSession = restoreDailyPartesFilters(preferences[PREF_CUADRILLAS_SESSION_STATE], filterDay);
				const remoteAdvancedFilters = preferences[PREF_CUADRILLAS_ADVANCED_FILTERS];
				const remoteTableColumns = preferences[PREF_CUADRILLAS_TABLE_COLUMNS];

				if (remoteSession && typeof remoteSession === 'object') {
					if (typeof remoteSession.textoBusqueda === 'string') setTextoBusqueda(remoteSession.textoBusqueda);
					if (remoteSession.fechaDesde) setFechaDesde(remoteSession.fechaDesde);
					if (remoteSession.fechaHasta) setFechaHasta(remoteSession.fechaHasta);
					if (remoteSession.filtroAplicado && typeof remoteSession.filtroAplicado === 'object') {
						setFiltroAplicado(remoteSession.filtroAplicado);
					}
					if (remoteSession.itemsPerPage) setItemsPerPage(Number(remoteSession.itemsPerPage || 25));
					if (remoteSession.currentPage) setCurrentPage(Number(remoteSession.currentPage || 1));
					if (Object.prototype.hasOwnProperty.call(remoteSession, 'openFiltros')) {
						setOpenFiltros(Boolean(remoteSession.openFiltros));
					}
				}

				if (Array.isArray(remoteAdvancedFilters)) {
					setFiltrosAvanzadosCompartidos(
						appendLegacyOperativoFilter(remoteAdvancedFilters, 'todos')
					);
				}

				if (Array.isArray(remoteTableColumns)) {
					setVisibleTableColumns(normalizeVisibleTableColumns(remoteTableColumns));
				}
			} catch (error) {
				console.error('Error cargando preferencias remotas de cuadrillas:', error);
			} finally {
				if (active) {
					setRemotePreferencesReady(true);
				}
			}
		};

		loadRemotePreferences();

		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		if (!remotePreferencesReady) {
			return;
		}

		saveUsuarioPreferencias({
			[PREF_CUADRILLAS_SESSION_STATE]: {
				filterDay,
				textoBusqueda,
				fechaDesde,
				fechaHasta,
				filtroAplicado,
				itemsPerPage,
				currentPage,
				openFiltros,
			},
			[PREF_CUADRILLAS_ADVANCED_FILTERS]: filtrosAvanzadosCompartidos,
			[PREF_CUADRILLAS_TABLE_COLUMNS]: visibleTableColumns,
		}).catch((error) => {
			console.error('Error guardando preferencias remotas de cuadrillas:', error);
		});
	}, [
		currentPage,
		fechaDesde,
		fechaHasta,
		filtroAplicado,
		filtrosAvanzadosCompartidos,
		itemsPerPage,
		openFiltros,
		remotePreferencesReady,
		textoBusqueda,
		visibleTableColumns,
	]);

	const aplicarFiltroFechas = () => {
		if (!fechaDesde || !fechaHasta) {
			setErrorFechas('Debes indicar ambas fechas.');
			return;
		}

		if (fechaDesde > fechaHasta) {
			setErrorFechas('La fecha desde no puede ser mayor que la fecha hasta.');
			return;
		}

		setErrorFechas('');
		setCurrentPage(1);
		setFiltroAplicado({
			fechaDesde,
			fechaHasta,
		});
	};

	const limpiarFiltroFechas = () => {
		const desde = getDefaultFechaDesde();
		const hasta = getDefaultFechaHasta();

		setFechaDesde(desde);
		setFechaHasta(hasta);
		setTextoBusqueda('');
		setErrorFechas('');
		setCurrentPage(1);
		setFiltrosAvanzadosCompartidos((currentFilters) =>
			(Array.isArray(currentFilters) ? currentFilters : []).map((filterItem) => ({
				...filterItem,
				seleccionado: [],
			}))
		);
		setResetFiltrosAvanzadosToken((currentToken) => currentToken + 1);

		setFiltroAplicado({
			fechaDesde: desde,
			fechaHasta: hasta,
		});
	};

	const moverFechaDesde = (days) => {
		setFechaDesde((current) => shiftIsoDateByDays(current, days));
	};

	const moverFechaHasta = (days) => {
		setFechaHasta((current) => shiftIsoDateByDays(current, days));
	};

	const moverRangoFechas = (days) => {
		moverFechaDesde(days);
		moverFechaHasta(days);
	};

	const handlePageChange = (event, value) => {
		setCurrentPage(value);
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	};

	const cerrarFiltros = () => {
		setOpenFiltros(false);
	};

	const abrirPanelOperativos = () => {
		navigate('/cuadrillas/operativos', {
			state: {
				operativosReturnState: {
					textoBusqueda,
					fechaDesde,
					fechaHasta,
					filtroAplicado,
					filtroOperativo,
					openFiltros,
					advancedFilters: filtrosAvanzadosCompartidos,
				},
			},
		});
	};

	const actualizarAvatarRecolector = (lista, idCuadrilla, idUsuario, imagenUrl) => {
		return lista.map((cuadrilla) => {
			if (cuadrilla?.datos_cuadrilla?.id_cuadrilla !== idCuadrilla) {
				return cuadrilla;
			}

			const actualizarGrupo = (grupo = []) =>
				grupo.map((recolector) =>
					recolector?.id_usuario === idUsuario
						? { ...recolector, imagen_url: imagenUrl }
						: recolector
				);

			return {
				...cuadrilla,
				recolectores: {
					...cuadrilla.recolectores,
					no_comprobados: actualizarGrupo(cuadrilla.recolectores?.no_comprobados),
					comprobados: actualizarGrupo(cuadrilla.recolectores?.comprobados),
					error_comprobacion: actualizarGrupo(cuadrilla.recolectores?.error_comprobacion),
				},
			};
		});
	};

	const handleRecolectorAvatarUploaded = ({ idCuadrilla, idUsuario, imagenUrl }) => {
		setCuadrillas((currentCuadrillas) =>
			actualizarAvatarRecolector(currentCuadrillas, idCuadrilla, idUsuario, imagenUrl)
		);
		setCuadrillasOG((currentCuadrillas) =>
			actualizarAvatarRecolector(currentCuadrillas, idCuadrilla, idUsuario, imagenUrl)
		);
	};

	const actualizarEstadoParteEnLista = (lista, idCuadrilla, changes = {}) =>
		lista.map((cuadrilla) => {
			if (cuadrilla?.datos_cuadrilla?.id_cuadrilla !== idCuadrilla) {
				return cuadrilla;
			}

			return {
				...cuadrilla,
				datos_cuadrilla: {
					...cuadrilla.datos_cuadrilla,
					...changes,
				},
			};
		});

	const handleEstadoParteUpdated = ({ idCuadrilla, changes = {} }) => {
		if (!idCuadrilla || !changes || Object.keys(changes).length === 0) {
			return;
		}

		setCuadrillas((currentCuadrillas) =>
			actualizarEstadoParteEnLista(currentCuadrillas, idCuadrilla, changes)
		);
		setCuadrillasOG((currentCuadrillas) =>
			actualizarEstadoParteEnLista(currentCuadrillas, idCuadrilla, changes)
		);
	};

	const cuadrillasFiltradas = useMemo(() => {
		const termino = textoBusqueda.trim().toLowerCase();
		return cuadrillas.filter((cuadrilla) => {
			const datos = cuadrilla?.datos_cuadrilla ?? {};
			if (!termino) {
				return true;
			}

			const campos = [
				datos.id_cuadrilla,
				datos.nombre_cuadrilla,
				datos.nombre_cabo,
				datos.apellidos_cabo,
				datos.nombre_cliente,
				datos.nombre_delegacion,
				datos.nombre_campana,
			];

			return campos.some((campo) =>
				String(campo ?? '').toLowerCase().includes(termino)
			);
		});
	}, [cuadrillas, textoBusqueda]);

	const personaReturnState = useMemo(
		() => ({
			fechaDesde,
			fechaHasta,
			filtroAplicado,
			textoBusqueda,
			itemsPerPage,
			currentPage,
			visibleTableColumns,
		}),
		[currentPage, fechaDesde, fechaHasta, filtroAplicado, itemsPerPage, textoBusqueda, visibleTableColumns]
	);

	const selectedCuadrillasCount = useMemo(
		() => Object.values(selectedCuadrillas).filter(Boolean).length,
		[selectedCuadrillas]
	);

	const indexOfLastItem = currentPage * itemsPerPage;
	const indexOfFirstItem = indexOfLastItem - itemsPerPage;
	const currentCuadrillas = cuadrillasFiltradas.slice(indexOfFirstItem, indexOfLastItem);

	useEffect(() => {
		const reopenCuadrillaId = Number(restoreState?.reopenCuadrillaId || 0);

		if (!loaded || !reopenCuadrillaId || cuadrillasFiltradas.length === 0) {
			return;
		}

		const targetIndex = cuadrillasFiltradas.findIndex(
			(cuadrilla) =>
				Number(cuadrilla?.datos_cuadrilla?.id_cuadrilla || 0) === reopenCuadrillaId
		);

		if (targetIndex < 0) {
			return;
		}

		const targetPage = Math.floor(targetIndex / itemsPerPage) + 1;
		if (currentPage !== targetPage) {
			setCurrentPage(targetPage);
			return;
		}

		const pageStartIndex = (targetPage - 1) * itemsPerPage;
		const localIndex = targetIndex - pageStartIndex;

		setOpen((currentOpen) => (currentOpen === localIndex ? currentOpen : localIndex));
	}, [currentPage, cuadrillasFiltradas, itemsPerPage, loaded, restoreState?.reopenCuadrillaId]);

	const allCurrentPageSelected = useMemo(() => {
		if (currentCuadrillas.length === 0) {
			return false;
		}

		return currentCuadrillas.every((cuadrilla) =>
			Boolean(selectedCuadrillas[cuadrilla?.datos_cuadrilla?.id_cuadrilla])
		);
	}, [currentCuadrillas, selectedCuadrillas]);

	const someCurrentPageSelected = useMemo(
		() =>
			currentCuadrillas.some((cuadrilla) =>
				Boolean(selectedCuadrillas[cuadrilla?.datos_cuadrilla?.id_cuadrilla])
			),
		[currentCuadrillas, selectedCuadrillas]
	);

	const toggleCuadrillaSelection = (idCuadrilla) => {
		if (!idCuadrilla) {
			return;
		}

		setSelectedCuadrillas((current) => ({
			...current,
			[idCuadrilla]: !current[idCuadrilla],
		}));
	};

	const toggleCurrentPageSelection = (checked) => {
		setSelectedCuadrillas((current) => {
			const next = { ...current };

			currentCuadrillas.forEach((cuadrilla) => {
				const idCuadrilla = cuadrilla?.datos_cuadrilla?.id_cuadrilla;
				if (idCuadrilla) {
					next[idCuadrilla] = checked;
				}
			});

			return next;
		});
	};

	const handleVisibleTableColumnsChange = (event) => {
		const value = event?.target?.value;
		setVisibleTableColumns(
			normalizeVisibleTableColumns(typeof value === 'string' ? value.split(',') : value)
		);
	};

	const resetVisibleTableColumns = () => {
		setVisibleTableColumns(DEFAULT_PARTES_GRUPO_TABLE_COLUMNS);
	};

	const exportarCuadrillasSeleccionadas = async () => {
		const selectedIds = cuadrillasOG
			.map((cuadrilla) => Number(cuadrilla?.datos_cuadrilla?.id_cuadrilla || 0))
			.filter((idCuadrilla) => idCuadrilla > 0 && selectedCuadrillas[idCuadrilla]);

		if (selectedIds.length === 0) {
			setExportFeedback({
				severity: 'warning',
				message: 'Selecciona al menos un parte de grupo para exportar.',
			});
			return;
		}

		setExporting(true);
		setExportFeedback({
			severity: 'info',
			message: 'Preparando el Excel e incrustando las imagenes de fichaje...',
		});

		try {
			const results = await Promise.allSettled(
				selectedIds.map((idCuadrilla) => getDetalleParteGrupo(idCuadrilla))
			);

			const detallesExportables = [];
			let failedCount = 0;

			results.forEach((result) => {
				if (result.status !== 'fulfilled' || result.value?.success !== true) {
					failedCount += 1;
					return;
				}

				detallesExportables.push(result.value.data);
			});

			if (detallesExportables.length === 0) {
				setExportFeedback({
					severity: 'warning',
					message: 'No hay datos disponibles para exportar en los partes seleccionados.',
				});
				return;
			}

			const { workbook, stats } = await createPartesGrupoExcelWorkbook({
				detalles: detallesExportables,
				fechaDesde: filtroAplicado.fechaDesde,
				fechaHasta: filtroAplicado.fechaHasta,
				omittedPartes: failedCount,
			});
			const excelBuffer = await workbook.xlsx.writeBuffer();
			const blob = new Blob([excelBuffer], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});

			saveAs(
				blob,
				`partes-grupo-${filtroAplicado.fechaDesde}-${filtroAplicado.fechaHasta}.xlsx`
			);

			const messageParts = [
				`Excel generado con ${stats.totalRows} trabajador(es).`,
				`${stats.rowsWithEmbeddedImage} foto(s) de fichaje incrustada(s).`,
			];

			if (stats.rowsWithoutValidScore > 0) {
				messageParts.push(
					`${stats.rowsWithoutValidScore} trabajador(es) no tenian puntuacion valida y no requerian foto.`
				);
			}

			if (stats.rowsWithMissingSource > 0) {
				messageParts.push(
					`${stats.rowsWithMissingSource} fichaje(s) validos llegaron sin ruta de imagen.`
				);
			}

			if (stats.rowsWithFailedImage > 0) {
				messageParts.push(
					`${stats.rowsWithFailedImage} foto(s) no se pudieron incrustar tras varios intentos. Revisa la hoja Incidencias.`
				);
			}

			if (failedCount > 0) {
				messageParts.push(
					`${failedCount} parte(s) se omitieron porque no se pudo cargar su detalle.`
				);
			}

			setExportFeedback({
				severity:
					failedCount > 0 || stats.rowsWithMissingSource > 0 || stats.rowsWithFailedImage > 0
						? 'warning'
						: 'success',
				message: messageParts.join(' '),
			});
		} catch (error) {
			console.error('Error exportando partes de grupo:', error);
			setExportFeedback({
				severity: 'error',
				message: 'No se pudo generar el Excel de los partes seleccionados.',
			});
		} finally {
			setExporting(false);
		}
	};

	return (
		<Grid
			container
			direction="row"
			justifyContent="center"
			alignItems="center"
			size={{ xs: 12 }}
		>
			<Grid
				container
				direction="column"
				justifyContent="start"
				alignItems="start"
				size={{ xs: 12, md: 11.5 }}
				sx={{ mb: 1 }}
			>
				<Grid item size={{ xs: 12 }}>
					<SubHeader titulo={'Partes de grupos'} />
				</Grid>

				<Grid item size={{ xs: 12 }}>
					<Grid
						container
						direction="row"
						justifyContent="start"
						alignItems="end"
						size={{ xs: 12 }}
						spacing={2}
						sx={{ px: 2 }}
					>
						<Grid container size={{ xs: 12, md: "auto" }} alignItems="end" spacing={1}>
							<Grid item>
								<Button
									variant="text"
									size="small"
									onClick={() => moverRangoFechas(-1)}
									sx={{ minWidth: 0, px: 0.5 }}
									aria-label="Restar un dia al rango"
								>
									<ChevronLeftIcon />
								</Button>
							</Grid>
							<Grid item>
								<TextField
									variant="outlined"
									label="Desde"
									type="date"
									size="small"
									value={fechaDesde}
									onChange={(event) => setFechaDesde(event.target.value)}
									error={!!errorFechas && !fechasValidas}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>
							<Grid item>
								<TextField
									variant="outlined"
									label="Hasta"
									type="date"
									size="small"
									value={fechaHasta}
									onChange={(event) => setFechaHasta(event.target.value)}
									error={!!errorFechas && !fechasValidas}
									InputLabelProps={{ shrink: true }}
								/>
							</Grid>
							<Grid item>
								<Button
									variant="text"
									size="small"
									onClick={() => moverRangoFechas(1)}
									sx={{ minWidth: 0, px: 0.5 }}
									aria-label="Sumar un dia al rango"
								>
									<ChevronRightIcon />
								</Button>
							</Grid>
						</Grid>

						<TextField
							variant="standard"
							label="Buscar grupo"
							placeholder="ID, nombre, cabo o empresa"
							size="small"
							value={textoBusqueda}
							onChange={(event) => setTextoBusqueda(event.target.value)}
						/>

						<Button
							variant="contained"
							onClick={aplicarFiltroFechas}
							disabled={!fechaDesde || !fechaHasta}
						>
							Buscar
						</Button>

						<Button variant="outlined" onClick={limpiarFiltroFechas}>
							Limpiar
						</Button>

						<Badge badgeContent={cantidadFiltros} color="primary">
							<Button
								variant="outlined"
								onClick={() => {
									setOpenFiltros((currentOpen) => !currentOpen);
								}}
							>
								<FilterAltIcon sx={{ fontSize: 20, mr: 0.3, mt: -0.3 }} />
								filtros
							</Button>
						</Badge>

						<FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
							<InputLabel id="partes-grupo-columnas-label">Columnas</InputLabel>
							<Select
								labelId="partes-grupo-columnas-label"
								id="partes-grupo-columnas"
								multiple
								value={visibleTableColumns}
								label="Columnas"
								onChange={handleVisibleTableColumnsChange}
								renderValue={(selected) =>
									`${normalizeVisibleTableColumns(selected).length} visibles`
								}
							>
								{PARTES_GRUPO_TABLE_COLUMNS.map((column) => (
									<MenuItem key={column.key} value={column.key}>
										<Checkbox checked={visibleTableColumns.includes(column.key)} />
										<ListItemText primary={column.label} />
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<Button
							variant="text"
							size="small"
							startIcon={<ViewColumnIcon />}
							onClick={resetVisibleTableColumns}
							disabled={
								visibleTableColumns.length === DEFAULT_PARTES_GRUPO_TABLE_COLUMNS.length &&
								DEFAULT_PARTES_GRUPO_TABLE_COLUMNS.every((column) =>
									visibleTableColumns.includes(column)
								)
							}
						>
							Restaurar columnas
						</Button>

						{capabilities.canExportExcel && (
							<Button
								variant="outlined"
								onClick={exportarCuadrillasSeleccionadas}
								disabled={!loaded || exporting}
							>
								<FileDownloadIcon sx={{ fontSize: 20, mr: 0.3, mt: -0.3 }} />
								{exporting
									? 'Preparando Excel...'
									: selectedCuadrillasCount > 0
										? `Exportar (${selectedCuadrillasCount})`
										: 'Exportar'}
							</Button>
						)}

						{capabilities.canOpenOperativos && (
							<Button
								variant="outlined"
								onClick={abrirPanelOperativos}
							>
								<FactCheckIcon sx={{ fontSize: 20, mr: 0.3, mt: -0.3 }} />
								panel operativos
							</Button>
						)}
					</Grid>
				</Grid>

				<Grid item size={{ xs: 12 }}>
					<FiltrosCuadrillas
						open={openFiltros}
						onClose={cerrarFiltros}
						setCuadrillas={setCuadrillas}
						cuadrillasOG={cuadrillasOG}
						fechaDesde={filtroAplicado.fechaDesde}
						fechaHasta={filtroAplicado.fechaHasta}
						cantidadFiltros={cantidadFiltros}
						setCantidadFiltros={setCantidadFiltros}
						onFiltersChange={setFiltrosAvanzadosCompartidos}
						initialSelectedFilters={filtrosAvanzadosCompartidos}
						resetToken={resetFiltrosAvanzadosToken}
					/>
				</Grid>
			</Grid>

			<Grid
				container
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				size={{ xs: 12, md: 11.5 }}
				sx={{ mt: 1, mb: 1 }}
			>
				<Grid item size={{ xs: 12 }}>
					<Typography variant="body2" color="text.secondary">
						Rango aplicado:{' '}
						<strong>{formatDisplayDate(filtroAplicado.fechaDesde)}</strong>
						{' - '}
						<strong>{formatDisplayDate(filtroAplicado.fechaHasta)}</strong>
						{' · '}
						Operativo:{' '}
						<strong>
							{filtroOperativo === 'operativos'
								? 'Operativos'
								: filtroOperativo === 'no_operativos'
									? 'No operativos'
									: 'Todos'}
						</strong>
					</Typography>
				</Grid>

				{!!errorFechas && (
					<Grid item size={{ xs: 12 }} sx={{ mt: 1 }}>
						<Alert severity="warning">{errorFechas}</Alert>
					</Grid>
				)}

				{!!exportFeedback?.message && (
					<Grid item size={{ xs: 12 }} sx={{ mt: 1 }}>
						<Alert severity={exportFeedback.severity || 'info'}>
							{exportFeedback.message}
						</Alert>
					</Grid>
				)}
			</Grid>

			<Grid
				container
				direction="row"
				justifyContent="space-between"
				alignItems="start"
				size={{ xs: 12, md: 11.5 }}
			>
				<Grid
					container
					direction="row"
					justifyContent="center"
					alignItems="center"
					size={{ xs: 12 }}
				>
					{loaded ? (
						<Grid
							container
							direction="row"
							justifyContent={cuadrillasFiltradas.length > 0 ? 'start' : 'center'}
							alignItems={cuadrillasFiltradas.length > 0 ? 'start' : 'center'}
							size={{ xs: 12 }}
							className="fade-in barra-scroll"
							sx={{ overflow: 'auto', minHeight: '55vh', maxHeight: '70vh' }}
						>
							{cuadrillasFiltradas.length > 0 ? (
								<TablaCuadrillas
									currentCuadrillas={currentCuadrillas}
									setOpen={setOpen}
									open={open}
									capabilities={capabilities}
									onRecolectorAvatarUploaded={handleRecolectorAvatarUploaded}
									onEstadoParteUpdated={handleEstadoParteUpdated}
									personaReturnState={personaReturnState}
									selectedCuadrillas={selectedCuadrillas}
									onToggleCuadrillaSelection={toggleCuadrillaSelection}
									onToggleCurrentPageSelection={toggleCurrentPageSelection}
									allCurrentPageSelected={allCurrentPageSelected}
									someCurrentPageSelected={someCurrentPageSelected}
									visibleColumnKeys={visibleTableColumns}
								/>
							) : (
								<Grid
									container
									direction="row"
									justifyContent="center"
									alignItems="center"
									size={{ xs: 12 }}
									className="fade-in"
								>
									<Alert
										icon={<CloudOffIcon fontSize="inherit" color="warning" />}
										severity="warning"
									>
										No se encontraron grupos.
									</Alert>
								</Grid>
							)}
						</Grid>
					) : (
						<Grid
							container
							direction="row"
							justifyContent="center"
							alignItems="center"
							size={{ xs: 12 }}
							sx={{ py: 3, minHeight: '50vh' }}
							className="fade-in"
						>
							<LinearProgress sx={{ width: '100%' }} />
						</Grid>
					)}
				</Grid>

				<Grid
					container
					direction="row"
					justifyContent="space-between"
					alignItems="end"
					size={{ xs: 12 }}
					sx={{ mt: 1, display: cuadrillasFiltradas.length > 0 ? 'flex' : 'none' }}
				>
					<Grid
						container
						direction="row"
						justifyContent="start"
						alignItems="center"
						size={{ xs: 12, md: 3 }}
					>
						<Grid item size={{ xs: 4 }}>
							<FormControl variant="standard" fullWidth>
								<InputLabel id="filas-por-pagina-label">Filas por página</InputLabel>
								<Select
									labelId="filas-por-pagina-label"
									id="filas-por-pagina"
									value={itemsPerPage}
									label="Filas por página"
									onChange={(event) => {
										setItemsPerPage(Number(event.target.value));
										setCurrentPage(1);
									}}
								>
									<MenuItem value={25}>25</MenuItem>
									<MenuItem value={50}>50</MenuItem>
									<MenuItem value={75}>75</MenuItem>
									<MenuItem value={100}>100</MenuItem>
								</Select>
							</FormControl>
						</Grid>
					</Grid>

					<Pagination
						count={Math.ceil(cuadrillasFiltradas.length / itemsPerPage)}
						page={currentPage}
						onChange={handlePageChange}
						color="primary"
						sx={{
							display:
								cuadrillasFiltradas.length > 0 && itemsPerPage < cuadrillasFiltradas.length
									? 'flex'
									: 'none',
						}}
					/>
				</Grid>
			</Grid>

		</Grid>
	);
};
